import type { AxiosError } from 'axios';

/**
 * Extract user-friendly error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  // Axios error with response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<{
      message?: string;
      error?: string | { code?: string; message?: string }
    }>;

    // Backend error message
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    if (axiosError.response?.data?.error) {
      const errorData = axiosError.response.data.error;
      // Handle both string and object error formats
      if (typeof errorData === 'string') {
        return errorData;
      }
      if (typeof errorData === 'object' && errorData.message) {
        return errorData.message;
      }
    }

    // HTTP status code messages
    const status = axiosError.response?.status;
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in.';
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'Validation failed. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable.';
      case 503:
        return 'Service temporarily unavailable.';
      default:
        return `An error occurred${status ? ` (${status})` : ''}`;
    }
  }

  // Network error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as Error).message;
    if (message.includes('Network Error')) {
      return 'Network error. Please check your connection.';
    }
    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    return message;
  }

  return 'An unexpected error occurred';
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as AxiosError).response?.status;
  }
  return undefined;
}
